import { useCallback, useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import type { UseVoiceSessionReturn } from '@orborix/voice-client';
import { setSessionIncognito } from '../../../lib/sessionsApi.js';

export interface UseIncognitoReturn {
  incognito: boolean;
  toggleIncognito: () => void;
  /** Valor actual para callbacks estables (p.ej. `leave`). */
  incognitoRef: RefObject<boolean>;
}

/**
 * Charla privada (incógnito): estado EFÍMERO por sesión, NO se persiste
 * (a diferencia de `settings`, que son prefs de DB). El caso real es el toggle
 * mid-sesión → POST /sessions/:id/incognito; el stop handler relee el flag al
 * cerrar para saltar memoria + analyze. Best-effort: un POST fallido no rompe
 * la conversación (igual mostramos el badge).
 */
export function useIncognito(args: {
  sessionRef: RefObject<UseVoiceSessionReturn | null>;
  sessionId: string | null;
}): UseIncognitoReturn {
  const { sessionRef, sessionId } = args;

  const [incognito, setIncognito] = useState(false);
  const incognitoRef = useRef(incognito);
  incognitoRef.current = incognito;

  const toggleIncognito = useCallback((): void => {
    setIncognito((prev) => {
      const next = !prev;
      const sid = sessionRef.current?.sessionId;
      if (sid) void setSessionIncognito(sid, next).catch(() => undefined);
      return next;
    });
  }, [sessionRef]);

  // Si el user prendió incógnito ANTES de que la sesión tuviera id, sincronizamos
  // el flag al backend en cuanto el id aparece.
  useEffect(() => {
    if (incognito && sessionId) void setSessionIncognito(sessionId, true).catch(() => undefined);
  }, [sessionId, incognito]);

  return { incognito, toggleIncognito, incognitoRef };
}
